/**
 * A reseller curating what ONE of its customers reads on the support portal
 * (2026-09-30, the CEO: "each reseller can select what documents customer see
 * based on what documents reseller can see him self").
 *
 * The decision is portal-api's (`Documents::AccessResolver`, the reseller
 * layer). This file is the BFF's answer to the SPA — camelCase, like every
 * other projection in this package; the snake_case contract with portal-api
 * (`/api/v1/reseller/customers/:customer_id/document_access`) lives in
 * `apps/server/src/routes/customerDocumentationAccess.ts` and stops there.
 *
 * THREE CHOICES, and the third is not "off". `null` is DEFAULT: the reseller
 * says nothing and the customer reads whatever XOP publishes for the products
 * it owns. `allow` shares a document (or a folder, which reaches everything
 * under it that has no nearer rule); `deny` hides it. Two hard limits sit
 * above every choice, both enforced by portal-api: a document XOP ruled for
 * this customer (`lockedByXop`) is XOP's, and a document the customer portal
 * does not publish (`publishedToCustomers: false`) cannot be shared into it.
 */

/** What a reseller rule does. */
export const DOC_ACCESS_EFFECTS = ["allow", "deny"] as const;

export type DocAccessEffect = (typeof DOC_ACCESS_EFFECTS)[number];

/** A row's choice: a rule, or `null` = Default (no rule of the reseller's). */
export type DocAccessChoice = DocAccessEffect | null;

/** What a rule sits on — portal-api's polymorphic `target_type`, verbatim,
 * because it is also the key the PUT body is keyed by. */
export const DOC_ACCESS_TARGET_TYPES = ["Document", "DocumentCategory"] as const;

export type DocAccessTargetType = (typeof DOC_ACCESS_TARGET_TYPES)[number];

/** portal-api's per-request ceiling, mirrored so the SPA never builds a save
 * the BFF would refuse. */
export const MAX_DOC_ACCESS_RULE_CHANGES = 500;

export function isDocAccessEffect(v: unknown): v is DocAccessEffect {
  return typeof v === "string" && (DOC_ACCESS_EFFECTS as readonly string[]).includes(v);
}

/** One folder the reseller can read something in (or a parent of one). */
export interface CustomerDocAccessCategory {
  id: number;
  /** `null` for a top-level folder — and also treated as top-level by the
   * tree when it names a folder that is not in the list. */
  parentId: number | null;
  name: string;
  slug: string;
  /** The reseller's rule stored ON this folder, if any. */
  resellerRule: DocAccessChoice;
  /** XOP has a rule on this folder for this customer: the reseller cannot
   * change it. */
  lockedByXop: boolean;
}

/** One document the RESELLER can read — nothing it cannot read is ever
 * listed, so it can never share what it could not see itself. */
export interface CustomerDocAccessDocument {
  id: number;
  title: string;
  slug: string;
  docType: string | null;
  categoryId: number | null;
  shelf: "library" | "release";
  /** The version a release document ships with, for its chip. */
  releaseVersion: string | null;
  /** portal-api's verdict for THIS customer, with every rule stored today. */
  customerVisible: boolean;
  /** Why (`general`, `reseller_allow`, `staff_deny`, …) — portal-api's
   * verdict reason, carried as a string so a new reason renders rather than
   * breaking the page. */
  reason: string | null;
  resellerRule: DocAccessChoice;
  /** An XOP (staff) rule decides this document for this customer. */
  lockedByXop: boolean;
  /** Passes the customer portal's library layer. False ⇒ nobody below XOP
   * can open it to this customer. */
  publishedToCustomers: boolean;
}

export interface CustomerDocumentationAccess {
  customer: {
    /** The CRM customer id — the one the reseller's customer page uses. */
    crmCustomerId: string;
    name: string;
    /** portal-api's `doc_access_mode` for the organisation (`auto`,
     * `strict`, …) — shown, never decided on here. */
    docAccessMode: string | null;
  };
  categories: CustomerDocAccessCategory[];
  documents: CustomerDocAccessDocument[];
}

/** One change in a save. `effect: null` deletes the reseller's rule on that
 * target (back to Default). */
export interface CustomerDocAccessRuleChange {
  targetType: DocAccessTargetType;
  targetId: number;
  effect: DocAccessChoice;
}

export interface CustomerDocAccessRulesRequest {
  rules: CustomerDocAccessRuleChange[];
}

/**
 * The typed refusals the BFF answers with, so the page can say something a
 * person can act on rather than echoing a status.
 */
export const CUSTOMER_DOC_ACCESS_ERRORS = {
  /** The customer is not on the support portal yet — or is not this
   * reseller's. portal-api answers both with the SAME 404 on purpose (no
   * oracle), and so does this. */
  notOnPortal: "customer_not_on_portal",
  /** A save named a document or folder the reseller cannot read. Nothing was
   * written. */
  targetNotVisible: "target_not_visible",
  /** portal-api or crm-api could not be reached. */
  unavailable: "documentation_unavailable",
  /** portal-api could not tell which reseller company is asking. */
  unidentified: "reseller_unidentified",
} as const;
